import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface WLLSFileUploadProps {
  onFileUploaded: (fileId: string) => void;
}

export const WLLSFileUpload: React.FC<WLLSFileUploadProps> = ({ onFileUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [minLength, setMinLength] = useState(3);
  const [maxLength, setMaxLength] = useState(7);
  const [minFrequency, setMinFrequency] = useState(2);
  const { user } = useAuth();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);

    try {
      // Read file content
      const csvText = await file.text();
      const lines = csvText.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      // Parse CSV data
      const csvData = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          csvData.push(row);
        }
      }

      // Validate required columns
      const requiredColumns = ['Type', 'P&L USD'];
      const missingColumns = requiredColumns.filter(col => !headers.includes(col));
      
      if (missingColumns.length > 0) {
        toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
        return;
      }

      // Create file record
      const { data: fileRecord, error: fileError } = await supabase
        .from('wlls_files')
        .insert({
          filename: file.name,
          file_size: file.size,
          raw_data: csvData,
          analysis_status: 'pending'
        })
        .select()
        .single();

      if (fileError) throw fileError;

      onFileUploaded(fileRecord.id);
      
      // Reset file input
      event.target.value = '';
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload and analyze file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload WLLS File
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Analysis Parameters */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="minLength">Min Length</Label>
            <Input
              id="minLength"
              type="number"
              value={minLength}
              onChange={(e) => setMinLength(parseInt(e.target.value))}
              min={2}
              max={10}
            />
          </div>
          <div>
            <Label htmlFor="maxLength">Max Length</Label>
            <Input
              id="maxLength"
              type="number"
              value={maxLength}
              onChange={(e) => setMaxLength(parseInt(e.target.value))}
              min={2}
              max={10}
            />
          </div>
          <div>
            <Label htmlFor="minFrequency">Min Freq</Label>
            <Input
              id="minFrequency"
              type="number"
              value={minFrequency}
              onChange={(e) => setMinFrequency(parseInt(e.target.value))}
              min={1}
              max={100}
            />
          </div>
        </div>

        {/* File Upload */}
        <div>
          <Label htmlFor="csvFile">CSV File</Label>
          <Input
            id="csvFile"
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </div>

        {uploading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading and analyzing file...
          </div>
        )}
      </CardContent>
    </Card>
  );
};